import React, { createContext, useContext, useState } from "react";
import { getDejavooKey } from "../plugins/ApiHandler";

const SCRIPT_URL = "https://payment.ipospays.tech/ftd/v1/freedomtodesign.js";
const SCRIPT_ID = "ftd";
const SCRIPT_LOAD_TIMEOUT = 2000;

const DejavooPaymentContext = createContext();

const cleanup = () => {
  try {
    const existingScript = document.getElementById(SCRIPT_ID);
    existingScript?.parentNode?.removeChild(existingScript);
    delete window.postData;
    delete window.currentOrigin;
  } catch (error) {
    console.error("Error during cleanup:", error);
  }
};

const loadDejavooScript = (authKey) => {
  return new Promise((resolve, reject) => {
    try {
      if (window.postData && typeof window.postData === "function") {
        return resolve(window.postData);
      }

      if (!authKey) throw new Error("Security key not available");

      cleanup();
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.type = "text/javascript";
      script.src = SCRIPT_URL;
      script.setAttribute("security_key", authKey);

      script.onload = () => {
        setTimeout(() => {
          if (window.postData && typeof window.postData === "function") {
            resolve(window.postData);
          } else {
            cleanup();
            reject(new Error("Payment system not initialized"));
          }
        }, SCRIPT_LOAD_TIMEOUT);
      };

      script.onerror = () => {
        cleanup();
        reject(new Error("Failed to load payment script"));
      };

      document.body.appendChild(script);
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
};

export const DejavooPaymentProvider = ({ children }) => {
  const [dejavooState, setDejavooState] = useState({
    status: "uninitialized",
    error: null,
    postData: null,
    tpn: null,
    authKey: null,
  });

  const handleTransactionResponse = (response) => {
    if (!response) {
      setDejavooState((prev) => ({
        ...prev,
        error: "No response received from payment system",
      }));
      return false;
    }

    setDejavooState((prev) => ({ ...prev, error: null }));
    return true;
  };

  const initializeDejavoo = async (companyId) => {
    if (!companyId) {
      console.error("DejavooPayment: No companyId provided");
      return;
    }

    try {
      setDejavooState((prev) => ({ ...prev, status: "loading", error: null }));
      const response = await getDejavooKey(companyId);

      if (!response?.authKey || !response?.tpn) {
        throw new Error("Failed to get payment configuration");
      }

      const postDataFn = await loadDejavooScript(response.authKey);
      setDejavooState({
        status: "loaded",
        error: null,
        postData: postDataFn,
        tpn: response.tpn,
        authKey: response.authKey,
      });
    } catch (error) {
      setDejavooState((prev) => ({
        ...prev,
        status: "error",
        error: error.message,
        postData: null,
      }));
    }
  };

  const resetDejavoo = () => {
    cleanup();
    setDejavooState({
      status: "uninitialized",
      error: null,
      postData: null,
      tpn: null,
      authKey: null,
    });
  };

  return (
    <DejavooPaymentContext.Provider
      value={{
        dejavoo: {
          ...dejavooState,
          initialize: initializeDejavoo,
          reset: resetDejavoo,
          handleTransactionResponse,
        },
      }}
    >
      {children}
    </DejavooPaymentContext.Provider>
  );
};

export const useDejavooPayment = () => {
  const context = useContext(DejavooPaymentContext);
  if (!context) {
    throw new Error(
      "useDejavooPayment must be used within DejavooPaymentProvider"
    );
  }
  return context;
};
